import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@workspace/ui/components/sonner';
import { m } from '~/paraglide/messages.js';
import { createApiKey, getApiKeys, revokeApiKey } from '../-api/api';
import type { CreateApiKeyPayload } from '../-api/types';

const KEYS_QUERY_KEY = ['api-keys'] as const;

export function useApiKeys() {
  return useQuery({
    queryKey: KEYS_QUERY_KEY,
    queryFn: getApiKeys,
  });
}

export function useCreateApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateApiKeyPayload) => createApiKey(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
      toast.success(m['developer.api_keys.toast.create_success']());
    },
    onError: (error: Error) => {
      const msg = error.message;
      if (msg === 'E_SCOPE_ESCALATION') {
        toast.error(m['developer.api_keys.toast.scope_escalation']());
      } else if (msg === 'E_RATE_LIMIT') {
        toast.error(m['developer.api_keys.toast.rate_limit']());
      } else {
        toast.error(m['developer.api_keys.toast.create_error']());
      }
      console.error('[useCreateApiKey]', { operation: 'create', error: msg });
    },
  });
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS_QUERY_KEY });
      toast.success(m['developer.api_keys.toast.revoke_success']());
    },
    onError: (error: Error) => {
      toast.error(m['developer.api_keys.toast.revoke_error']());
      console.error('[useRevokeApiKey]', {
        operation: 'revoke',
        error: error.message,
      });
    },
  });
}
